import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import styled from "styled-components";
import Table from "../../../components/Table/Table";
import MatterListSkeleton from "../../../components/Table/MatterListSkeleton";
import { MatterTableColumn } from "../../../components/Table/columns/MatterTableColumn";
import PillDropdown from "../../../components/PillDropdown/PillDropdown";
import Pagination from "../../../components/Pagination/Pagination";
import { fetchMatter } from "../../../service/matter";

const CaseListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TableArea = styled.div`
  position: relative;
  min-height: 400px;
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 0;
`;

// The user's own cases (backend `assigned_to_me` matches the logged-in user
// against principal attorney and case manager). Same table as the Matters
// page, but sort/filter/pagination state is local to this tab so it never
// disturbs the Matters page's redux-backed view.
const CaseListTab = ({ handleError }) => {
  const navigate = useNavigate();

  const [cases, setCases] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState("Open");
  const [sort, setSort] = useState("asc");
  const [sortItem, setSortItem] = useState("display_name");
  const [isLoading, setIsLoading] = useState(true);

  const tableColumns = MatterTableColumn({
    sortState: { sort, item: sortItem },
    onSortChange: (nextSort, nextItem) => {
      setSort(nextSort);
      setSortItem(nextItem);
      setCurrentPage(1);
    },
    searchTerm: "",
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const response = await fetchMatter(
          currentPage,
          status === "All" ? "" : status,
          "",
          sort,
          sortItem,
          pageSize,
          "",
          true
        );
        if (cancelled) return;
        setCases(response?.data?.results ?? []);
        setTotalRecords(response?.data?.count ?? 0);
      } catch (err) {
        console.error("Failed to fetch case list", err);
        if (!cancelled && handleError) {
          handleError("Failed to fetch your case list");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, status, sort, sortItem]);

  const getRowProps = (record) => ({
    onClick: (e) => {
      e.stopPropagation();
      if (record.last_opened_relative_path) {
        navigate(`/matter${record.last_opened_relative_path}`, {
          state: { record },
        });
      } else {
        navigate(`/matter/${record.id}`, { state: { record } });
      }
    },
  });

  return (
    <CaseListWrapper>
      <ControlsRow>
        <PillDropdown
          handleChange={(value) => {
            setStatus(value);
            setCurrentPage(1);
          }}
          value={status}
          testId="case-list-status-dropdown"
          options={[
            { value: "Open", label: "Open" },
            { value: "Closed", label: "Closed" },
            { value: "All", label: "All" },
          ]}
          placeholder="Status"
        />
      </ControlsRow>
      <TableArea>
        <Table
          columns={tableColumns}
          data={cases}
          loading={isLoading}
          loadingComponent={<MatterListSkeleton rows={pageSize || 10} />}
          onRow={getRowProps}
          emptyMessage="No cases found"
        />
        {!isLoading && cases.length > 0 && (
          <PaginationRow>
            <Pagination
              total={totalRecords}
              current={currentPage}
              pageSize={pageSize}
              onChange={(page) => setCurrentPage(page)}
              adjustable={false}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </PaginationRow>
        )}
      </TableArea>
    </CaseListWrapper>
  );
};

CaseListTab.propTypes = {
  handleError: PropTypes.func,
};

export default CaseListTab;
